import {
  Body,
  Box,
  Button,
  Container,
  Icon,
  Link,
  SimpleGrid,
  TextInput,
} from '@hover/blueprint';
import { iArrowRight } from '@hover/icons';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import React from 'react';
import { useForm } from 'react-hook-form';
import type { WindowWithChilipiper } from '@hover/shared-components/src/types/windowWithChilipiper';

export type SDRLeadFormData = {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  company: string;
  utm_source: string;
  campaignMedium: string;
  campaignSource: string;
};

interface ISDRLeadFormProps {
  campaignName?: string;
}

const FORM_ID = 'chili-piper-form';

const domains = {
  prod: 'hover',
  staging: 'hover-to',
};

export const SDRLeadForm: React.FC<ISDRLeadFormProps> = ({ campaignName }) => {
  const onSubmit = (data: SDRLeadFormData) => {
    const domain =
      process.env.GATSBY_ENVIRONMENT === 'production'
        ? domains.prod
        : domains.staging;
    (window as WindowWithChilipiper).ChiliPiper?.submit(domain, 'inbound', {
      title: 'Thanks! What time works best for a quick call?',
      formId: FORM_ID,
      lead: {
        FirstName: data.firstName,
        LastName: data.lastName,
        Email: data.email,
        Phone: data.mobile,
        Company: data.company,
        Campaign_Name_CP__c: campaignName || data.utm_source,
        Campaign_Medium_CP__c: data.campaignMedium,
        Campaign_Source_CP__c: data.campaignSource,
      },
    });
  };

  const location = useLocation();
  const utmSource = location.search
    ? queryString.parse(location.search).utm_source
    : '';
  const campaignMedium = location.search
    ? queryString.parse(location.search).campaignMedium
    : '';
  const campaignSource = location.search
    ? queryString.parse(location.search).campaignSource
    : '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SDRLeadFormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  return (
    <Container
      bgColor="primary.50"
      borderRadius={8}
      as="form"
      maxW="3xl"
      mb={{ base: 1000, desktop: '220px' }}
      onSubmit={handleSubmit(onSubmit)}
      p={{ base: 6, desktop: 12 }}
      pb={6}
    >
      <Body fontSize="10px" isBold letterSpacing="0.15em" mb={6}>
        STEP 1 OF 2
      </Body>

      <SimpleGrid
        columns={1}
        spacingX={600}
        spacingY={{ base: 400, desktop: 500 }}
      >
        <input
          type="hidden"
          value={utmSource as string}
          {...register('utm_source')}
        />
        <input
          type="hidden"
          value={campaignMedium as string}
          {...register('campaignMedium')}
        />
        <input
          type="hidden"
          value={campaignSource as string}
          {...register('campaignSource')}
        />

        <TextInput
          placeholder="FIRST NAME*"
          {...register('firstName', {
            required: true,
          })}
          error={!!errors.firstName}
        />
        <TextInput
          placeholder="LAST NAME*"
          {...register('lastName', {
            required: true,
          })}
          error={!!errors.lastName}
        />
        <TextInput
          placeholder="EMAIL*"
          type="email"
          {...register('email', {
            required: true,
            pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/,
          })}
          error={!!errors.email}
        />
        <TextInput
          placeholder="MOBILE PHONE*"
          type="tel"
          {...register('mobile', {
            required: true,
            pattern:
              /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,5})|(\(?\d{2,6}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/,
          })}
          error={!!errors.mobile}
        />

        <TextInput
          placeholder="COMPANY NAME*"
          {...register('company', {
            required: true,
          })}
          error={!!errors.company}
        />
      </SimpleGrid>

      <Box mt={500}>
        <Button
          type="submit"
          d="flex"
          alignItems="center"
          justifyContent="center"
          pr={400}
          size="large"
          mt={{ base: 600, desktop: 0 }}
          isFullWidth
        >
          <Body isBold size={400} color="neutral.0">
            PICK A TIME TO MEET
          </Body>
          <Icon size="large" icon={iArrowRight} color="neutral.0" />
        </Button>
      </Box>

      <Body
        mt={{ base: 400, desktop: 600 }}
        size={400}
        fontStyle="italic"
        color="brandNavy.300"
      >
        By submitting this form, you agree to permit HOVER to contact you via
        phone, email, or text message, which may be automated, regarding HOVER
        products and other promotions and have read and acknowledge our{' '}
        <Link href="/privacy">Privacy Policy</Link>. Consent is not a condition
        of any purchase and may be revoked at any time.
      </Body>
    </Container>
  );
};
